import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, ArrowPathIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { updateDetectionRule, fetchTenants, suppressAlertTitle, unsuppressAlertTitle, suppressAlertDescription, unsuppressAlertDescription, suppressAlertActor, unsuppressAlertActor } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import CopyButton from './CopyButton';
import MspScopeSelect from './ui/MspScopeSelect';

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'informational'];

const BLUE_ALLIANCE_TENANT_ID = '752b8092-f529-4eff-a6e8-179e3309ec1e';

// Rule-specific: only rule_new_country_signin reads suppressed_event_country_codes today.
const NEW_COUNTRY_SIGNIN_FN = 'rule_new_country_signin';
// Defender security_alert pass-through rules read parameters_json.excluded_alert_titles
// and parameters_json.excluded_alert_descriptions.
const DEFENDER_PASSTHROUGH_FN = 'rule_defender_endpoint_alert_passthrough';
// The batched insider-threat rule coalesces per-tenant entries into one config, so MSP
// codes there would apply batch-wide with first-entry semantics (handoff). Hide MSP scope.
const MAILBOX_NON_OWNER_FN = 'rule_mailbox_accessed_by_non_owner';
// Datalist suggestions for the country code picker. Free text still allowed —
// pipeline normalizes case to upper on read; any 2-letter ISO code works.
const COMMON_COUNTRY_CODES = [
  'US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE',
  'CH', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'PT', 'IN', 'JP', 'KR', 'SG',
  'HK', 'CN', 'MX', 'BR', 'AR', 'ZA', 'AE', 'IL',
];

// excluded_alert_titles entry: plain string (exact) or { title, mode:'contains' }.
function normalizeTitleEntry(e) {
  if (e == null) return null;
  if (typeof e === 'string') return e.trim() ? { title: e, mode: 'exact' } : null;
  if (typeof e === 'object') {
    const t = (e.title ?? '').toString();
    return t.trim() ? { title: t, mode: (e.mode || 'exact').toString().toLowerCase() } : null;
  }
  return null;
}

// Local Administrators Group Change: the pipeline's `suppress_domain_join` switch (default ON when
// absent; `false` or "false" turns it off). It lives in parameters_json, so the toggle edits the
// same JSON the box below shows. Turning it on removes the key, leaving the pipeline's default.
function DomainJoinToggle({ rawParamsJson, setRawParamsJson }) {
  let parsed = null;
  try {
    const p = JSON.parse(rawParamsJson || '{}');
    if (p && typeof p === 'object' && !Array.isArray(p)) parsed = p;
  } catch { /* invalid JSON: toggle disabled until the box is fixed */ }
  const on = !parsed || !(parsed.suppress_domain_join === false
    || String(parsed.suppress_domain_join).toLowerCase() === 'false');
  const toggle = () => {
    if (!parsed) return;
    const next = { ...parsed };
    if (on) next.suppress_domain_join = false; else delete next.suppress_domain_join;
    setRawParamsJson(JSON.stringify(next, null, 2));
  };
  return (
    <label className={`flex items-start gap-2 mb-3 ${parsed ? 'cursor-pointer' : 'opacity-50'}`}>
      <input type="checkbox" checked={on} onChange={toggle} disabled={!parsed} className="mt-0.5" />
      <span className="text-sm text-gray-300">
        Suppress domain joins
        <span className="block text-xs text-gray-500">
          Don't alert when Domain Admins is added to a computer's local Administrators group as part of the
          computer joining the domain (Domain Users added to local Users on the same computer, by the same
          account, within 2 minutes). On by default.
        </span>
      </span>
    </label>
  );
}

function parseExcludedTitles(parametersJson) {
  try {
    const p = JSON.parse(parametersJson || '{}');
    const raw = Array.isArray(p.excluded_alert_titles)
      ? p.excluded_alert_titles
      : (p.excluded_alert_titles ? [p.excluded_alert_titles] : []);
    return raw.map(normalizeTitleEntry).filter(Boolean);
  } catch { return []; }
}

// excluded_alert_descriptions entry: plain string (exact, no detector gate) or
// { description, mode:'contains'?, detector_id? }.
function normalizeDescriptionEntry(e) {
  if (e == null) return null;
  if (typeof e === 'string') return e.trim() ? { description: e, mode: 'exact', detectorId: '' } : null;
  if (typeof e === 'object') {
    const d = (e.description ?? '').toString();
    if (!d.trim()) return null;
    // API returns camelCase detectorId; pipeline JSON uses snake_case detector_id.
    const di = (e.detectorId ?? e.detector_id ?? '').toString();
    return { description: d, mode: (e.mode || 'exact').toString().toLowerCase(), detectorId: di };
  }
  return null;
}

function parseExcludedDescriptions(parametersJson) {
  try {
    const p = JSON.parse(parametersJson || '{}');
    const raw = Array.isArray(p.excluded_alert_descriptions)
      ? p.excluded_alert_descriptions
      : (p.excluded_alert_descriptions ? [p.excluded_alert_descriptions] : []);
    return raw.map(normalizeDescriptionEntry).filter(Boolean);
  } catch { return []; }
}

// excluded_alert_actors entry: plain string (exact) or { actor, mode:'contains' }.
function normalizeActorEntry(e) {
  if (e == null) return null;
  if (typeof e === 'string') return e.trim() ? { actor: e, mode: 'exact' } : null;
  if (typeof e === 'object') {
    const a = (e.actor ?? '').toString();
    if (!a.trim()) return null;
    return { actor: a, mode: (e.mode || 'exact').toString().toLowerCase() };
  }
  return null;
}

function parseExcludedActors(parametersJson) {
  try {
    const p = JSON.parse(parametersJson || '{}');
    const raw = Array.isArray(p.excluded_alert_actors)
      ? p.excluded_alert_actors
      : (p.excluded_alert_actors ? [p.excluded_alert_actors] : []);
    return raw.map(normalizeActorEntry).filter(Boolean);
  } catch { return []; }
}

// Managed list for a Defender pass-through rule's excluded_alert_titles. Adds/removes
// go straight through the dedicated SuppressAlertTitle endpoint (works on PROD rules —
// a narrow list mutation, not part of the PROD-disabled drawer Save), so this renders
// in both the PROD read-only and TEST editable branches. The endpoint returns the
// updated list; we coerce defensively against PowerShell single-element-array unwrap.
function ExcludedAlertTitlesManager({ rule }) {
  const [titles, setTitles]   = useState(() => parseExcludedTitles(rule?.parametersJson));
  const [newTitle, setNewTitle] = useState('');
  const [newMode, setNewMode]   = useState('exact');
  const [busyKey, setBusyKey]   = useState(null); // 'add' | `${title}|${mode}`
  const [error, setError]       = useState(null);

  const coerce = (r) => {
    const v = r?.excludedAlertTitles;
    const arr = Array.isArray(v) ? v : (v ? [v] : []);
    return arr.map(normalizeTitleEntry).filter(Boolean);
  };

  const handleAdd = async () => {
    const t = newTitle.trim();
    if (!t) return;
    setBusyKey('add'); setError(null);
    try {
      const res = await suppressAlertTitle(rule.ruleId, { title: t, mode: newMode });
      setTitles(coerce(res));
      setNewTitle('');
    } catch (e) { setError(e.message || 'Failed to add title'); }
    finally { setBusyKey(null); }
  };

  const handleRemove = async (entry) => {
    const key = `${entry.title}|${entry.mode}`;
    setBusyKey(key); setError(null);
    try {
      const res = await unsuppressAlertTitle(rule.ruleId, { title: entry.title, mode: entry.mode });
      setTitles(coerce(res));
    } catch (e) { setError(e.message || 'Failed to remove title'); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="border border-rose-800/40 bg-rose-950/10 rounded-lg p-4 space-y-3">
      <div>
        <p className="text-sm font-medium text-rose-200">Excluded Alert Titles</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Defender alerts whose title matches are dropped by this rule, across every tenant the rule applies to.
          Changes take effect ~10 min after the last rule edit, when the detection streaming job restarts.
        </p>
      </div>

      {titles.length === 0 ? (
        <p className="text-xs text-gray-500 italic">No excluded titles.</p>
      ) : (
        <ul className="space-y-1">
          {titles.map((e) => {
            const key = `${e.title}|${e.mode}`;
            return (
              <li key={key} className="flex items-center gap-2 text-sm bg-gray-800/50 rounded px-2 py-1 border border-gray-700/50">
                <span className="text-gray-200 break-all flex-1">{e.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${e.mode === 'contains' ? 'bg-amber-900/30 text-amber-300 border-amber-700/40' : 'bg-gray-700/40 text-gray-300 border-gray-600/40'}`}>
                  {e.mode}
                </span>
                <button onClick={() => handleRemove(e)} disabled={busyKey === key}
                  title="Remove this excluded title" className="text-gray-500 hover:text-red-400 disabled:opacity-50 p-0.5">
                  {busyKey === key ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <TrashIcon className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Alert title to exclude" className="input-field flex-1 text-sm" />
        <select value={newMode} onChange={(e) => setNewMode(e.target.value)} className="input-field text-sm w-28">
          <option value="exact">exact</option>
          <option value="contains">contains</option>
        </select>
        <button onClick={handleAdd} disabled={busyKey === 'add' || !newTitle.trim()}
          className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50">
          {busyKey === 'add' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
          Add
        </button>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

// Managed list for a Defender pass-through rule's excluded_alert_descriptions. Sibling of
// ExcludedAlertTitlesManager but matches on $.description (+ optional detector_id gate).
// Adds/removes go through the dedicated SuppressAlertDescription endpoint (works on PROD
// rules — a narrow list mutation), so it renders in both the PROD read-only and TEST
// editable branches.
function ExcludedAlertDescriptionsManager({ rule }) {
  const [descs, setDescs]         = useState(() => parseExcludedDescriptions(rule?.parametersJson));
  const [newDesc, setNewDesc]     = useState('');
  const [newMode, setNewMode]     = useState('contains');
  const [newDetector, setNewDetector] = useState('');
  const [busyKey, setBusyKey]     = useState(null); // 'add' | `${description}|${mode}|${detectorId}`
  const [error, setError]         = useState(null);

  const keyOf = (e) => `${e.description}|${e.mode}|${e.detectorId || ''}`;

  const coerce = (r) => {
    const v = r?.excludedAlertDescriptions;
    const arr = Array.isArray(v) ? v : (v ? [v] : []);
    return arr.map(normalizeDescriptionEntry).filter(Boolean);
  };

  const handleAdd = async () => {
    const d = newDesc.trim();
    if (!d) return;
    setBusyKey('add'); setError(null);
    try {
      const res = await suppressAlertDescription(rule.ruleId, { description: d, mode: newMode, detectorId: newDetector.trim() });
      setDescs(coerce(res));
      setNewDesc(''); setNewDetector('');
    } catch (e) { setError(e.message || 'Failed to add description'); }
    finally { setBusyKey(null); }
  };

  const handleRemove = async (entry) => {
    const key = keyOf(entry);
    setBusyKey(key); setError(null);
    try {
      const res = await unsuppressAlertDescription(rule.ruleId, { description: entry.description, mode: entry.mode, detectorId: entry.detectorId || '' });
      setDescs(coerce(res));
    } catch (e) { setError(e.message || 'Failed to remove description'); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="border border-rose-800/40 bg-rose-950/10 rounded-lg p-4 space-y-3">
      <div>
        <p className="text-sm font-medium text-rose-200">Excluded Alert Descriptions</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Defender alerts whose <span className="font-mono">description</span> matches are dropped by this rule, across every tenant the rule applies to.
          Matched case-insensitively after trimming (no wildcards / regex). Set a detector ID to pin the drop to one alert type.
          Changes take effect within ~2 min.
        </p>
      </div>

      {descs.length === 0 ? (
        <p className="text-xs text-gray-500 italic">No excluded descriptions.</p>
      ) : (
        <ul className="space-y-1">
          {descs.map((e) => {
            const key = keyOf(e);
            return (
              <li key={key} className="flex items-center gap-2 text-sm bg-gray-800/50 rounded px-2 py-1 border border-gray-700/50">
                <div className="flex-1 min-w-0">
                  <span className="text-gray-200 break-all">{e.description}</span>
                  {e.detectorId && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-sky-900/30 text-sky-300 border-sky-700/40 break-all">
                      detector: {e.detectorId}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${e.mode === 'contains' ? 'bg-amber-900/30 text-amber-300 border-amber-700/40' : 'bg-gray-700/40 text-gray-300 border-gray-600/40'}`}>
                  {e.mode}
                </span>
                <button onClick={() => handleRemove(e)} disabled={busyKey === key}
                  title="Remove this excluded description" className="text-gray-500 hover:text-red-400 disabled:opacity-50 p-0.5">
                  {busyKey === key ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <TrashIcon className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="space-y-2">
        <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Alert description text to exclude" className="input-field w-full text-sm" />
        <div className="flex items-center gap-2">
          <input type="text" value={newDetector} onChange={(e) => setNewDetector(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Detector ID (optional)" className="input-field flex-1 text-sm" />
          <select value={newMode} onChange={(e) => setNewMode(e.target.value)} className="input-field text-sm w-28">
            <option value="contains">contains</option>
            <option value="exact">exact</option>
          </select>
          <button onClick={handleAdd} disabled={busyKey === 'add' || !newDesc.trim()}
            className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50">
            {busyKey === 'add' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
            Add
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

// Managed list for a Defender pass-through rule's excluded_alert_actors (v1.123.2). Third
// sibling of the titles/descriptions managers — matches on the alert's EVIDENCE ACTOR
// (userAccount.userPrincipalName → primaryAddress → upn) for Microsoft-substrate service
// accounts. Adds/removes go through the dedicated SuppressAlertActor endpoint (works on
// PROD rules — a narrow list mutation), so it renders in both the PROD read-only and TEST
// editable branches.
function ExcludedAlertActorsManager({ rule }) {
  const [actors, setActors]   = useState(() => parseExcludedActors(rule?.parametersJson));
  const [newActor, setNewActor] = useState('');
  const [newMode, setNewMode]   = useState('exact');
  const [busyKey, setBusyKey]   = useState(null); // 'add' | `${actor}|${mode}`
  const [error, setError]       = useState(null);

  const keyOf = (e) => `${e.actor}|${e.mode}`;

  const coerce = (r) => {
    const v = r?.excludedAlertActors;
    const arr = Array.isArray(v) ? v : (v ? [v] : []);
    return arr.map(normalizeActorEntry).filter(Boolean);
  };

  const handleAdd = async () => {
    const a = newActor.trim();
    if (!a) return;
    setBusyKey('add'); setError(null);
    try {
      const res = await suppressAlertActor(rule.ruleId, { actor: a, mode: newMode });
      setActors(coerce(res));
      setNewActor('');
    } catch (e) { setError(e.message || 'Failed to add actor'); }
    finally { setBusyKey(null); }
  };

  const handleRemove = async (entry) => {
    const key = keyOf(entry);
    setBusyKey(key); setError(null);
    try {
      const res = await unsuppressAlertActor(rule.ruleId, { actor: entry.actor, mode: entry.mode });
      setActors(coerce(res));
    } catch (e) { setError(e.message || 'Failed to remove actor'); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="border border-rose-800/40 bg-rose-950/10 rounded-lg p-4 space-y-3">
      <div>
        <p className="text-sm font-medium text-rose-200">Excluded Alert Actors</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Defender alerts whose <span className="font-mono">evidence actor</span> matches are dropped by this rule, across every tenant the rule applies to.
          Matched case-insensitively after trimming (no wildcards / regex). Use only for Microsoft-substrate service accounts (e.g. <span className="font-mono">NT SERVICE\…</span>), never a real user.
          Changes take effect within ~2 min.
        </p>
      </div>

      {actors.length === 0 ? (
        <p className="text-xs text-gray-500 italic">No excluded actors.</p>
      ) : (
        <ul className="space-y-1">
          {actors.map((e) => {
            const key = keyOf(e);
            return (
              <li key={key} className="flex items-center gap-2 text-sm bg-gray-800/50 rounded px-2 py-1 border border-gray-700/50">
                <div className="flex-1 min-w-0">
                  <span className="text-gray-200 break-all font-mono">{e.actor}</span>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${e.mode === 'contains' ? 'bg-amber-900/30 text-amber-300 border-amber-700/40' : 'bg-gray-700/40 text-gray-300 border-gray-600/40'}`}>
                  {e.mode}
                </span>
                <button onClick={() => handleRemove(e)} disabled={busyKey === key}
                  title="Remove this excluded actor" className="text-gray-500 hover:text-red-400 disabled:opacity-50 p-0.5">
                  {busyKey === key ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <TrashIcon className="h-4 w-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <input type="text" value={newActor} onChange={(e) => setNewActor(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="Evidence actor to exclude (e.g. NT SERVICE\MSExchangeAdminApiNetCore …)" className="input-field flex-1 text-sm font-mono" />
        <select value={newMode} onChange={(e) => setNewMode(e.target.value)} className="input-field text-sm w-28">
          <option value="exact">exact</option>
          <option value="contains">contains</option>
        </select>
        <button onClick={handleAdd} disabled={busyKey === 'add' || !newActor.trim()}
          className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-50">
          {busyKey === 'add' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
          Add
        </button>
      </div>

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

const IP_FLAG_OPTIONS = [
  { value: 'detections_tor', label: 'Tor exit node' },
  { value: 'detections_vpn', label: 'VPN' },
  { value: 'detections_proxy', label: 'Proxy' },
  { value: 'detections_compromised', label: 'Compromised / botnet' },
  { value: 'detections_anonymous', label: 'Anonymous' },
  { value: 'detections_hosting', label: 'Hosting / datacenter' },
  { value: 'detections_scraper', label: 'Scraper' },
];

const EVENT_TYPE_OPTIONS = ['signin', 'graph_signin'];

const LOG_TYPE_OPTIONS = [
  { value: 'signin', label: 'Sign-in' },
  { value: 'graph_signin', label: 'Graph Sign-in' },
  { value: 'directory_audit', label: 'Directory Audit' },
  { value: 'exchange', label: 'Exchange' },
  { value: 'sharepoint', label: 'SharePoint' },
  { value: 'general', label: 'General' },
  { value: 'dlp', label: 'DLP' },
  { value: 'risk_detection', label: 'Risk Detection' },
  { value: 'risky_user', label: 'Risky User' },
  { value: 'ca_policy', label: 'CA Policy' },
  { value: 'security_alert', label: 'Security Alert' },
  { value: 'security_incident', label: 'Security Incident' },
  { value: 'secure_score', label: 'Secure Score' },
  { value: 'azure_activity', label: 'Azure Activity' },
  { value: 'message_trace', label: 'Message Trace' },
  { value: 'url_click', label: 'URL Click' },
];

const MATCH_OPERATORS = ['equals', 'contains', 'starts_with', 'not_equals'];

const BASELINE_WINDOW_OPTIONS = [
  { value: '7d', label: '7 days (default)' },
  { value: '30d', label: '30 days' },
];

const FIELD_OPTIONS = [
  { value: 'UserId', label: 'User (UPN)' },
  { value: 'ClientIP', label: 'Client IP' },
  { value: 'Operation', label: 'Operation' },
  { value: 'ResultStatus', label: 'Result Status' },
  { value: 'ApplicationId', label: 'Application ID' },
  { value: 'AppDisplayName', label: 'App Display Name' },
  { value: 'Workload', label: 'Workload' },
];

// JSON path fields for log_type_match rules — these resolve against raw_event_json via $.
// The pipeline does a substring contains/equals on the serialized JSON string (not JSONPath eval),
// so case matters and the $ prefix is literal.
const JSON_PATH_FIELD_OPTIONS = [
  { value: '$.ExtendedProperties', label: 'ExtendedProperties (JSON path)' },
  { value: '$.AppAccessContext.AADSessionId', label: 'AppAccessContext.AADSessionId (JSON path)' },
  { value: '$.ClientInfoString', label: 'ClientInfoString (JSON path)' },
  { value: '$.ClientUserAgent', label: 'ClientUserAgent (JSON path)' },
];

// Known value options for fields with enumerable values
const FIELD_VALUE_OPTIONS = {
  Workload: ['AzureActiveDirectory', 'Exchange', 'SharePoint', 'OneDrive', 'MicrosoftTeams', 'SecurityComplianceCenter', 'Sway', 'PowerBI', 'Yammer'],
};

// ResultStatus values differ by workload — verified from data 2026-04-08.
// Pipeline lowercases both sides on comparison, so case-folding is handled automatically.
// Returning null means no dropdown — falls back to free-text input.
function getResultStatusOptions(ruleType, logType) {
  if (ruleType === 'ip_flag_match') {
    // ip_flag_match targets signin (stores 'Success') and graph_signin (null — no status field)
    return ['Success', 'Failed'];
  }
  if (ruleType === 'log_type_match' || ruleType === 'python_function_config') {
    if (logType === 'signin') return ['Success', 'Failed'];
    if (logType === 'exchange') return ['Succeeded', 'Failed'];
    if (logType === 'directory_audit') return ['success', 'failure'];
    if (logType === 'risk_detection' || logType === 'risky_user') return ['remediated', 'atRisk'];
    if (logType === 'security_incident') return ['resolved', 'active'];
    // graph_signin, sharepoint — null ResultStatus in data; free-text so user isn't misled
    if (logType === 'graph_signin' || logType === 'sharepoint') return null;
    // security_alert has no result status field
    if (logType === 'security_alert') return null;
    // general: mixed 'Success'/'Succeeded' — offer both
    if (logType === 'general') return ['Success', 'Succeeded', 'Failed'];
  }
  return ['Success', 'Failed'];
}

function getFieldValueOptions(fieldName, ruleType, logType) {
  if (fieldName === 'ResultStatus') return getResultStatusOptions(ruleType, logType);
  return FIELD_VALUE_OPTIONS[fieldName] || null;
}

// Cheat sheet for the 'general' log type — verified from Databricks query 2026-04-08.
// 'general' is a heterogeneous bucket: values differ by product/operation, not by success/failure concept.
const GENERAL_RESULT_STATUS_REFERENCE = [
  {
    value: 'Success',
    description: 'Teams, Planner, Tasks, Power Automate flows, Compliance cmdlets, Quarantine (hyphenated: Get-/Release-/Preview-/Delete-/Export-), Dynamics CRM',
    examples: ['TaskListRead', 'PlanListRead', 'CrmDefaultActivity', 'ChatMessageCreated', 'Get-QuarantineMessage', 'New-DlpCompliancePolicy', 'CreateFlow', 'EditFlow', 'Delete-QuarantineMessage'],
  },
  {
    value: 'Succeeded',
    description: 'Power BI, Fabric/Lakehouse, Forms, Power Apps, Copilot, file ops (CreateFile, RenameFileOrDirectory, DeleteFileOrBlob), security alerts, most admin and bot actions',
    examples: ['CopilotInteraction', 'RequestCopilot', 'CreateFile', 'RunArtifact', 'EditForm', 'LaunchPowerApp', 'AlertTriggered', 'AlertUpdated', 'TeamsAdminAction', 'BotCreate'],
  },
  {
    value: 'Successful',
    description: 'Quarantine operations without hyphen prefix, TeamsUserSignedOut',
    examples: ['QuarantineReleaseMessage', 'QuarantineDeleteMessage', 'QuarantinePreviewMessage', 'QuarantineExportMessage', 'QuarantineRequestReleaseMessage', 'TeamsUserSignedOut'],
  },
  {
    value: 'TRUE',
    description: 'Yammer and some activity feed events — literal string "TRUE", not a boolean',
    examples: ['ThreadViewed', 'MarkedMessageChanged', 'FileVisited', 'MessageCreation', 'O365SyncAdminUserPromotion'],
  },
  {
    value: 'New / Resolved / Dismissed',
    description: 'MCAS (Defender for Cloud Apps) alert lifecycle — not a login outcome',
    examples: ['MCAS_ALERT_ANUBIS_DETECTION_NEW_COUNTRY', 'MCAS_ALERT_ANUBIS_DETECTION_VELOCITY'],
  },
  {
    value: 'InProgress',
    description: 'Fabric artifact operations still executing at log time',
    examples: ['CreateArtifact (in-flight)', 'ReadArtifact (in-flight)'],
  },
  {
    value: 'AuthorizationFailure / Failure / Error / Failed',
    description: 'Failure variants — each product uses its own string alongside its success value',
    examples: ['PlanListRead → AuthorizationFailure', 'TaskRead → Failure', 'Get-ComplianceCaseMember → Error', 'CreateFile → Failed', 'ViewForm → Failed'],
  },
];

function getResultStatusHint(ruleType, logType) {
  if (ruleType === 'ip_flag_match') {
    return {
      text: 'signin stores "Success" (not "Succeeded"). graph_signin has no ResultStatus — events there will not match this filter.',
      hasRef: false,
    };
  }
  if (ruleType === 'log_type_match' || ruleType === 'python_function_config') {
    const hints = {
      signin: { text: 'signin stores "Success" (verified from data).', hasRef: false },
      exchange: { text: 'exchange stores "Succeeded" (verified from data).', hasRef: false },
      directory_audit: { text: 'directory_audit stores "success" / "failure" (lowercase). Pipeline comparison is case-insensitive.', hasRef: false },
      risk_detection: { text: 'Stores risk lifecycle state — "remediated" or "atRisk". Not a login outcome.', hasRef: false },
      risky_user: { text: 'Stores risk lifecycle state — "remediated" or "atRisk". Not a login outcome.', hasRef: false },
      security_incident: { text: 'Stores incident lifecycle state — "resolved" or "active".', hasRef: false },
      graph_signin: { text: 'graph_signin has no ResultStatus data — this filter will never match.', hasRef: false },
      sharepoint: { text: 'sharepoint has no ResultStatus data — this filter will never match.', hasRef: false },
      security_alert: { text: 'security_alert stores severity, not an outcome — this filter will not match as expected.', hasRef: false },
      general: { text: 'Values vary by operation type.', hasRef: true },
    };
    return hints[logType] || null;
  }
  return null;
}

// JSON path syntax hint for log_type_match fields (Databricks handoff 2026-04-08).
// Returns null when we don't have a specific recommendation for the log type.
function getFieldSyntaxHint(logType) {
  const GRAPH = new Set(['risk_detection','graph_signin','directory_audit','risky_user','security_alert','security_incident','secure_score','ca_policy']);
  const O365  = new Set(['exchange','sharepoint','signin','general','dlp','message_trace']);
  if (GRAPH.has(logType))     return { api: 'Graph API',      caseStyle: 'camelCase',  example: '$.riskLevel' };
  if (O365.has(logType))      return { api: 'O365 Audit',     caseStyle: 'PascalCase', example: '$.ClientInfoString' };
  if (logType === 'azure_activity') return { api: 'Azure Activity', caseStyle: 'camelCase',  example: '$.httpRequest.clientIpAddress' };
  if (logType === 'url_click')      return { api: 'URL Click',      caseStyle: 'PascalCase', example: '$.Url' };
  return null;
}

function parseParametersJson(json) {
  try { return JSON.parse(json || '{}'); } catch { return {}; }
}

export default function EditRuleDrawer({ isOpen, rule, onClose, onSuccess }) {
  const { user } = useAuth();
  const [ruleName, setRuleName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [enabled, setEnabled] = useState(true);
  const [cooldownMinutes, setCooldownMinutes] = useState(60);
  // ip_flag_match structured fields
  const [ipFlag, setIpFlag] = useState('');
  const [eventTypes, setEventTypes] = useState(['signin', 'graph_signin']);
  // log_type_match structured fields
  const [logType, setLogType] = useState('');
  const [matchConditions, setMatchConditions] = useState([]);
  // baseline_deviation params
  const [baselineField, setBaselineField] = useState('country_list');
  // operation_baseline_deviation params
  const [opBaselineLogType, setOpBaselineLogType] = useState('');
  const [opBaselineOperation, setOpBaselineOperation] = useState('');
  // python_function raw JSON
  const [rawParamsJson, setRawParamsJson] = useState('{}');

  const [skipIfEntraJoined, setSkipIfEntraJoined] = useState(false);
  const [skipIfCompliantDevice, setSkipIfCompliantDevice] = useState(false);
  const [skipIfDrmmMatch, setSkipIfDrmmMatch] = useState(false);
  const [skipIfIntuneManaged, setSkipIfIntuneManaged] = useState(false);
  const [skipIfInTenantBaseline, setSkipIfInTenantBaseline] = useState(false);
  const [skipIfInUserBaseline, setSkipIfInUserBaseline] = useState(false);
  const [skipIfProviderInUserBaseline, setSkipIfProviderInUserBaseline] = useState(false);
  const [providerBaselineMinIps, setProviderBaselineMinIps] = useState(2);
  const [skipIfCtapTrust, setSkipIfCtapTrust] = useState(false);
  const [skipIfInMailboxPairAllowlist, setSkipIfInMailboxPairAllowlist] = useState(false);
  const [skipIfTeamsPhoneEnrollment, setSkipIfTeamsPhoneEnrollment] = useState(false);
  const [skipIfInFileHashAllowlist, setSkipIfInFileHashAllowlist] = useState(false);
  const [skipIfInDefenderPassthroughIpAllowlist, setSkipIfInDefenderPassthroughIpAllowlist] = useState(false);
  const [skipIfInUserAppPairAllowlist, setSkipIfInUserAppPairAllowlist] = useState(false);
  const [skipIfInGroupAppAllowlist, setSkipIfInGroupAppAllowlist] = useState(false);
  const [skipIpv6PrefixMatch, setSkipIpv6PrefixMatch] = useState(false);
  const [overrideIfMaliciousIp, setOverrideIfMaliciousIp] = useState(false);
  // dedup_on_event_id — editable in BOTH TEST and PROD modes (no demotion required); it only
  // changes alert-collapse behavior, not detection logic.
  const [dedupOnEventId, setDedupOnEventId] = useState(false);

  // Baseline-read knob — lives inside parametersJson, default to current behavior if absent.
  // `baseline_ip_column` is auto-derived from window=30d + skipIpv6PrefixMatch; no separate picker.
  const [baselineWindow, setBaselineWindow] = useState('7d');

  // Rule scope: tenant UUIDs to include/exclude (top-level) + IP providers to exclude (inside parametersJson)
  const [includedTenantIds, setIncludedTenantIds] = useState([]);   // array of UUID strings
  const [excludedTenantIds, setExcludedTenantIds] = useState([]);   // array of UUID strings
  const [includedMspCodes, setIncludedMspCodes] = useState([]);     // array of uppercase MSP codes
  const [excludedMspCodes, setExcludedMspCodes] = useState([]);     // array of uppercase MSP codes
  const [excludedIpProviders, setExcludedIpProviders] = useState([]); // array of provider strings
  const [providerInput, setProviderInput] = useState('');             // chip-input buffer
  const [tenantList, setTenantList] = useState([]);                   // fetched on open

  // rule_new_country_signin specific: suppressed_event_country_codes inside parametersJson
  const [suppressedCountryCodes, setSuppressedCountryCodes] = useState([]); // array of ISO-2 strings
  const [countryInput, setCountryInput] = useState('');                     // chip-input buffer

  const [tested, setTested] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showResultStatusRef, setShowResultStatusRef] = useState(false);

  // Fetch tenant list once when drawer opens (for the Exclude Tenants multi-select)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetchTenants()
      .then((list) => { if (!cancelled) setTenantList(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setTenantList([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Populate form when rule changes
  useEffect(() => {
    if (rule) {
      setRuleName(rule.ruleName || '');
      setDescription(rule.description || '');
      setSeverity(rule.severity || 'medium');
      setEnabled(rule.enabled !== false);
      setTested(rule.tested === true);
      setCooldownMinutes(rule.cooldownMinutes ?? 60);
      setSkipIfEntraJoined(rule.skipIfEntraJoined ?? false);
      setSkipIfCompliantDevice(rule.skipIfCompliantDevice ?? false);
      setSkipIfDrmmMatch(rule.skipIfDrmmMatch ?? false);
      setSkipIfIntuneManaged(rule.skipIfIntuneManaged ?? false);
      setSkipIfInTenantBaseline(rule.skipIfInTenantBaseline ?? false);
      setSkipIfInUserBaseline(rule.skipIfInUserBaseline ?? false);
      setSkipIfProviderInUserBaseline(rule.skipIfProviderInUserBaseline ?? false);
      setSkipIfCtapTrust(rule.skipIfCtapTrust ?? false);
      setSkipIfInMailboxPairAllowlist(rule.skipIfInMailboxPairAllowlist ?? false);
      setSkipIfTeamsPhoneEnrollment(rule.skipIfTeamsPhoneEnrollment ?? false);
      setSkipIfInFileHashAllowlist(rule.skipIfInFileHashAllowlist ?? false);
      setSkipIfInDefenderPassthroughIpAllowlist(rule.skipIfInDefenderPassthroughIpAllowlist ?? false);
      setSkipIfInUserAppPairAllowlist(rule.skipIfInUserAppPairAllowlist ?? false);
      setSkipIfInGroupAppAllowlist(rule.skipIfInGroupAppAllowlist ?? false);
      setSkipIpv6PrefixMatch(rule.skipIpv6PrefixMatch ?? false);
      setOverrideIfMaliciousIp(rule.overrideIfMaliciousIp ?? false);
      setDedupOnEventId(rule.dedupOnEventId ?? false);
      // Rule scope
      const excludeCsv = (rule.excludedTenantIds || '').toString();
      setExcludedTenantIds(excludeCsv ? excludeCsv.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const includeCsv = (rule.includedTenantIds || '').toString();
      setIncludedTenantIds(includeCsv ? includeCsv.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const excludeMspCsv = (rule.excludedMspCodes || '').toString();
      setExcludedMspCodes(excludeMspCsv ? excludeMspCsv.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : []);
      const includeMspCsv = (rule.includedMspCodes || '').toString();
      setIncludedMspCodes(includeMspCsv ? includeMspCsv.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : []);
      setProviderInput('');
      setSaveError(null);

      const params = parseParametersJson(rule.parametersJson);
      setExcludedIpProviders(Array.isArray(params.excluded_ip_providers) ? params.excluded_ip_providers : []);
      setBaselineWindow(params.baseline_window === '30d' ? '30d' : '7d');
      const minIps = Number(params.provider_baseline_min_ips);
      setProviderBaselineMinIps(Number.isFinite(minIps) && minIps >= 1 ? minIps : 2);
      // suppressed_event_country_codes — currently only rule_new_country_signin reads it,
      // but we hydrate unconditionally so a clone or cross-rule edit doesn't silently drop it.
      const rawCountries = Array.isArray(params.suppressed_event_country_codes)
        ? params.suppressed_event_country_codes
        : [];
      const cleanedCountries = [];
      const seenCountries = new Set();
      for (const code of rawCountries) {
        if (typeof code !== 'string') continue;
        const upper = code.trim().toUpperCase();
        if (upper.length !== 2 || seenCountries.has(upper)) continue;
        seenCountries.add(upper);
        cleanedCountries.push(upper);
      }
      setSuppressedCountryCodes(cleanedCountries);
      setCountryInput('');
      if (rule.ruleType === 'ip_flag_match') {
        setIpFlag(params.ip_flag || '');
        setEventTypes(params.event_types || ['signin', 'graph_signin']);
        setMatchConditions(Array.isArray(params.match_conditions) ? params.match_conditions : []);
        setLogType('');
      } else if (rule.ruleType === 'log_type_match') {
        setLogType(params.log_type || '');
        setMatchConditions(Array.isArray(params.match_conditions) ? params.match_conditions : []);
        setIpFlag('');
        setEventTypes(['signin', 'graph_signin']);
        setBaselineField('country_list');
      } else if (rule.ruleType === 'python_function_config') {
        // Pipeline-defined arbitrary JSON — edit via raw textarea, preserve all keys.
        // Forcing the log_type_match form silently drops fields like vip_mailboxes,
        // tenant_filter, operations, result_statuses on save.
        setRawParamsJson(rule.parametersJson || '{}');
        setIpFlag('');
        setLogType('');
        setMatchConditions([]);
        setEventTypes(['signin', 'graph_signin']);
        setBaselineField('country_list');
      } else if (rule.ruleType === 'baseline_deviation') {
        setBaselineField(params.baseline_field || 'country_list');
        setIpFlag('');
        setLogType('');
        setMatchConditions([]);
        setEventTypes(Array.isArray(params.event_types) && params.event_types.length > 0 ? params.event_types : ['signin']);
      } else if (rule.ruleType === 'operation_baseline_deviation') {
        setOpBaselineLogType(params.log_type || '');
        // Pipeline reads plural `operations` (array). Older portal saves used
        // singular `operation` — fall back so legacy rules hydrate cleanly.
        // Display as comma-separated; the form is a single textbox.
        const opsArr = Array.isArray(params.operations)
          ? params.operations
          : (params.operation ? [params.operation] : []);
        setOpBaselineOperation(opsArr.join(', '));
        setIpFlag('');
        setLogType('');
        setMatchConditions([]);
        setEventTypes(['signin', 'graph_signin']);
        setBaselineField('country_list');
      } else {
        // Strip the manager-owned suppression lists from the raw box — they're edited
        // via the Excluded Alert Titles / Descriptions managed lists below and are
        // authoritative-from-server on save (UpdateDetectionRule restores them from the
        // current row, so any edit here is ignored). Showing them here as if editable is
        // misleading. Pretty-print the remainder for readability. (v1.110.2)
        let displayParams = rule.parametersJson || '{}';
        try {
          const parsed = JSON.parse(displayParams);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            delete parsed.excluded_alert_titles;
            delete parsed.excluded_alert_descriptions;
            delete parsed.excluded_alert_actors;
            displayParams = JSON.stringify(parsed, null, 2);
          }
        } catch { /* leave the raw string as-is if it isn't parseable JSON */ }
        setRawParamsJson(displayParams);
      }
    }
  }, [rule]);

  const isTest = rule?.mode === 'TEST';
  const isProd = rule?.mode === 'PROD';
  // PROD field-lock removed by request (2026-07-21): the entire rule is editable in both PROD
  // and TEST. `editable` drives all field gates; `isTest`/`isProd` now only drive the mode badge,
  // the TEST-only Scheduled Testing toggle, and the Promote/Demote buttons.
  const editable = true;

  const handleClose = () => {
    setSaveError(null);
    onClose();
  };

  const handleSave = async () => {
    setSaveError(null);

    // Build payload — the full field set in both modes (PROD lock removed).
    const payload = {};

    if (editable) {
      payload.ruleName = ruleName.trim();
      payload.description = description.trim();
      payload.severity = severity;
      payload.cooldownMinutes = Number(cooldownMinutes) || 0;
      payload.skipIfEntraJoined = skipIfEntraJoined;
      payload.skipIfCompliantDevice = skipIfCompliantDevice;
      payload.skipIfDrmmMatch = skipIfDrmmMatch;
      payload.skipIfIntuneManaged = skipIfIntuneManaged;
      payload.skipIfInTenantBaseline = skipIfInTenantBaseline;
      payload.skipIfInUserBaseline = skipIfInUserBaseline;
      payload.skipIfProviderInUserBaseline = skipIfProviderInUserBaseline;
      payload.skipIfCtapTrust = skipIfCtapTrust;
      payload.skipIfInMailboxPairAllowlist = skipIfInMailboxPairAllowlist;
      payload.skipIfTeamsPhoneEnrollment = skipIfTeamsPhoneEnrollment;
      payload.skipIfInFileHashAllowlist = skipIfInFileHashAllowlist;
      payload.skipIfInDefenderPassthroughIpAllowlist = skipIfInDefenderPassthroughIpAllowlist;
      payload.skipIfInUserAppPairAllowlist = skipIfInUserAppPairAllowlist;
      payload.skipIfInGroupAppAllowlist = skipIfInGroupAppAllowlist;
      payload.skipIpv6PrefixMatch = skipIpv6PrefixMatch;
      payload.overrideIfMaliciousIp = overrideIfMaliciousIp;
      payload.includedTenantIds = includedTenantIds.join(',');
      payload.excludedTenantIds = excludedTenantIds.join(',');
      payload.includedMspCodes = includedMspCodes.join(',');
      payload.excludedMspCodes = excludedMspCodes.join(',');

      if (!payload.ruleName) {
        setSaveError('Rule name is required.');
        return;
      }

      // Write baseline_window only when the user-baseline suppression is on AND
      // the window is non-default. Pipeline treats missing keys as 7d / signin_ip_list.
      // baseline_ip_column is auto-derived: when the user also has the IPv6 /64 toggle
      // on, we read from the precomputed prefix column (30-day table only).
      const stampBaseline = (p) => {
        if (skipIfInUserBaseline && baselineWindow === '30d') {
          p.baseline_window = '30d';
          if (skipIpv6PrefixMatch) {
            p.baseline_ip_column = 'signin_ip_prefix_list';
          }
        }
        return p;
      };

      // Write provider_baseline_min_ips only when the provider-in-user-baseline tier is on AND
      // the value is non-default. Pipeline treats a missing key as the default (2 distinct
      // baseline IPs backing a provider before it counts as "established").
      const stampProviderBaseline = (p) => {
        const n = Number(providerBaselineMinIps);
        if (skipIfProviderInUserBaseline && Number.isFinite(n) && n >= 1 && n !== 2) {
          p.provider_baseline_min_ips = n;
        }
        return p;
      };

      // Picker is gated on python_function_name === rule_new_country_signin, so for any other
      // rule the array is empty and this is a no-op. Currently only baseline_deviation rule
      // rule_75231a02 uses this, but we stamp it from every structured save branch in case the
      // pipeline ever moves the function to a different ruleType.
      const stampSuppressedCountries = (p) => {
        if (suppressedCountryCodes.length > 0) {
          p.suppressed_event_country_codes = suppressedCountryCodes;
        }
        return p;
      };

      // Build parametersJson
      if (rule.ruleType === 'ip_flag_match') {
        if (!ipFlag) {
          setSaveError('IP flag is required for ip_flag_match rules.');
          return;
        }
        const params = {
          ip_flag: ipFlag,
          event_types: eventTypes.length > 0 ? eventTypes : ['signin', 'graph_signin'],
        };
        const validConditions = matchConditions.filter((c) => c.field);
        if (validConditions.length > 0) params.match_conditions = validConditions;
        if (skipIfInTenantBaseline) params.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
        if (excludedIpProviders.length > 0) params.excluded_ip_providers = excludedIpProviders;
        stampBaseline(params);
        stampSuppressedCountries(params);
        stampProviderBaseline(params);
        payload.parametersJson = JSON.stringify(params);
      } else if (rule.ruleType === 'log_type_match') {
        if (!logType) {
          setSaveError('Log type is required for log_type_match rules.');
          return;
        }
        const params = { log_type: logType };
        const validConditions = matchConditions.filter((c) => c.field);
        if (validConditions.length > 0) params.match_conditions = validConditions;
        if (skipIfInTenantBaseline) params.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
        if (excludedIpProviders.length > 0) params.excluded_ip_providers = excludedIpProviders;
        stampBaseline(params);
        stampSuppressedCountries(params);
        stampProviderBaseline(params);
        payload.parametersJson = JSON.stringify(params);
      } else if (rule.ruleType === 'baseline_deviation') {
        if (!baselineField) {
          setSaveError('Baseline field is required for baseline_deviation rules.');
          return;
        }
        const params = {
          baseline_field: baselineField,
          event_types: eventTypes.length > 0 ? eventTypes : ['signin'],
        };
        if (skipIfInTenantBaseline) params.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
        if (excludedIpProviders.length > 0) params.excluded_ip_providers = excludedIpProviders;
        stampBaseline(params);
        stampSuppressedCountries(params);
        stampProviderBaseline(params);
        payload.parametersJson = JSON.stringify(params);
      } else if (rule.ruleType === 'operation_baseline_deviation') {
        if (!opBaselineLogType) {
          setSaveError('Log type is required for operation_baseline_deviation rules.');
          return;
        }
        const opsArr = opBaselineOperation
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (opsArr.length === 0) {
          setSaveError('At least one operation is required for operation_baseline_deviation rules.');
          return;
        }
        // Start from a copy of the original params so unknown keys (e.g. office_ip_threshold,
        // hand-tuned thresholds set by the pipeline team) survive a portal save. We only
        // overwrite keys the form owns; legacy singular `operation` is dropped in favour
        // of plural `operations` (which is what the pipeline reads).
        const original = parseParametersJson(rule.parametersJson);
        const params = { ...original };
        delete params.operation; // legacy singular form — replaced by plural operations
        delete params.suppression_tenant_id;
        delete params.excluded_ip_providers;
        delete params.baseline_window;
        delete params.baseline_ip_column;
        delete params.provider_baseline_min_ips;
        params.log_type      = opBaselineLogType;
        params.operations    = opsArr;
        params.baseline_field = 'signin_ip_list';
        if (skipIfInTenantBaseline) params.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
        if (excludedIpProviders.length > 0) params.excluded_ip_providers = excludedIpProviders;
        stampBaseline(params);
        stampSuppressedCountries(params);
        stampProviderBaseline(params);
        payload.parametersJson = JSON.stringify(params);
      } else {
        // Validate raw JSON, then inject suppression_tenant_id if toggle is on
        let parsedRaw;
        try {
          parsedRaw = JSON.parse(rawParamsJson);
        } catch {
          setSaveError('Parameters JSON is not valid JSON.');
          return;
        }
        const mutateRaw = parsedRaw && typeof parsedRaw === 'object' && !Array.isArray(parsedRaw);
        if (mutateRaw) {
          if (skipIfInTenantBaseline) parsedRaw.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
          if (excludedIpProviders.length > 0) {
            parsedRaw.excluded_ip_providers = excludedIpProviders;
          } else if ('excluded_ip_providers' in parsedRaw) {
            delete parsedRaw.excluded_ip_providers;
          }
          if (skipIfInUserBaseline && baselineWindow === '30d') {
            parsedRaw.baseline_window = '30d';
          } else if ('baseline_window' in parsedRaw) {
            delete parsedRaw.baseline_window;
          }
          if (skipIfInUserBaseline && baselineWindow === '30d' && skipIpv6PrefixMatch) {
            parsedRaw.baseline_ip_column = 'signin_ip_prefix_list';
          } else if ('baseline_ip_column' in parsedRaw) {
            delete parsedRaw.baseline_ip_column;
          }
          const rawMinIps = Number(providerBaselineMinIps);
          if (skipIfProviderInUserBaseline && Number.isFinite(rawMinIps) && rawMinIps >= 1 && rawMinIps !== 2) {
            parsedRaw.provider_baseline_min_ips = rawMinIps;
          } else if ('provider_baseline_min_ips' in parsedRaw) {
            delete parsedRaw.provider_baseline_min_ips;
          }
          // suppressed_event_country_codes — only meaningful for rule_new_country_signin,
          // but the picker is gated on python_function_name so the array is always [] for
          // other rules. Persist non-empty list; otherwise drop the key.
          if (suppressedCountryCodes.length > 0) {
            parsedRaw.suppressed_event_country_codes = suppressedCountryCodes;
          } else if ('suppressed_event_country_codes' in parsedRaw) {
            delete parsedRaw.suppressed_event_country_codes;
          }
          payload.parametersJson = JSON.stringify(parsedRaw);
        } else {
          payload.parametersJson = rawParamsJson;
        }
      }
    }

    // enabled + dedupOnEventId are editable in both modes (dedup only affects alert-collapse
    // behavior, not detection logic, so PROD rules can toggle it without demoting).
    payload.enabled = enabled;
    payload.dedupOnEventId = dedupOnEventId;

    setSaving(true);
    try {
      await updateDetectionRule(rule.ruleId, payload, rule.etag);
      onSuccess();
      onClose();
    } catch (err) {
      if (err.status === 412) {
        setSaveError('This rule was modified by another user. Please close and refresh to try again.');
      } else {
        setSaveError(err.message || 'Failed to save changes.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (newMode) => {
    const action = newMode === 'PROD' ? 'Promote' : 'Demote';
    const modeLabel = newMode === 'PROD' ? 'PROD' : 'TEST';
    const consequence = newMode === 'PROD'
      ? 'It will begin generating production alerts on the next pipeline run (up to 30 min).'
      : 'It will stop generating production alerts and only write to the test results table.';

    if (!window.confirm(`${action} rule "${rule.ruleName}" to ${modeLabel}?\n\n${consequence}`)) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = newMode === 'TEST' ? { mode: newMode, tested: false } : { mode: newMode };
      await updateDetectionRule(rule.ruleId, payload, rule.etag);
      onSuccess();
      onClose();
    } catch (err) {
      if (err.status === 412) {
        setSaveError('This rule was modified by another user. Please close and refresh to try again.');
      } else {
        setSaveError(err.message || `Failed to ${action.toLowerCase()} rule.`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestedToggle = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await updateDetectionRule(rule.ruleId, { tested: !tested }, rule.etag);
      setTested(!tested);
      onSuccess();
    } catch (err) {
      if (err.status === 412) {
        setSaveError('This rule was modified by another user. Please close and refresh to try again.');
      } else {
        setSaveError(err.message || 'Failed to update scheduled testing.');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleEventType = (et) => {
    setEventTypes((prev) =>
      prev.includes(et) ? prev.filter((t) => t !== et) : [...prev, et]
    );
  };

  const updateMatchCondition = (idx, key, value) => {
    setMatchConditions((prev) => prev.map((c, i) => {
      if (i !== idx) return c;
      const updated = { ...c, [key]: value };
      // Clear value when field changes (avoid stale value from previous field's dropdown)
      if (key === 'field') updated.value = '';
      return updated;
    }));
  };

  const addMatchCondition = () => {
    setMatchConditions((prev) => [...prev, { field: '', operator: 'equals', value: '' }]);
  };

  const removeMatchCondition = (idx) => {
    setMatchConditions((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCountryCode = () => {
    const upper = countryInput.trim().toUpperCase();
    if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) return;
    setSuppressedCountryCodes((prev) => (prev.includes(upper) ? prev : [...prev, upper]));
    setCountryInput('');
  };

  const removeCountryCode = (code) => {
    setSuppressedCountryCodes((prev) => prev.filter((c) => c !== code));
  };

  if (!isOpen || !rule) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-gray-100">Edit Rule</h2>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded ${
              isProd
                ? 'bg-green-900/40 text-green-400 border border-green-700/40'
                : 'bg-amber-900/40 text-amber-400 border border-amber-700/40'
            }`}>
              {rule.mode}
            </span>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Rule Name — editable in both modes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
              Rule Name {editable && <span className="text-red-400">*</span>}
            </label>
            {editable ? (
              <input
                type="text"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                className="input-field w-full"
              />
            ) : (
              <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">{rule.ruleName}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Rule Type</label>
              <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
                {rule.ruleType === 'ip_flag_match' ? 'IP Flag Match' : rule.ruleType === 'log_type_match' ? 'Log Type Match' : rule.ruleType === 'baseline_deviation' ? 'Baseline Deviation' : rule.ruleType === 'operation_baseline_deviation' ? 'Op + Baseline' : rule.ruleType === 'python_function_config' ? 'Config Function' : 'Python Function'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Version</label>
              <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
                v{rule.version}
              </p>
            </div>
          </div>

          {(rule.ruleType === 'python_function' || rule.ruleType === 'python_function_config') && rule.pythonFunctionName && (
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Python Function</label>
              <p className="text-sm text-gray-400 font-mono bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
                {rule.pythonFunctionName}
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created</label>
            <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
              {rule.createdUtc ? new Date(rule.createdUtc).toLocaleString() : '—'}
            </p>
          </div>

          <hr className="border-gray-800" />

          {/* Enabled toggle — always editable */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300">Enabled</label>
              <p className="text-xs text-gray-500">Disabled rules are skipped by the pipeline.</p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                enabled ? 'bg-blue-600' : 'bg-gray-700'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Rule Scope — tenant include + tenant exclusion + IP provider exclusion */}
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Rule Scope</label>

            {/* MSP scope — include/exclude by MSP code (all current & future tenants of the MSP).
                Hidden for the batched mailbox rule (first-entry semantics would apply batch-wide). */}
            {rule?.pythonFunctionName === MAILBOX_NON_OWNER_FN ? (
              <p className="text-xs text-gray-500 italic">
                MSP scoping is not available on this batched rule (it coalesces per-tenant entries, so an MSP code would apply batch-wide). Use the tenant lists instead.
              </p>
            ) : (
              <>
                <MspScopeSelect
                  label="Scope to these MSPs (include)"
                  help="Applies this rule to every current and future tenant of the selected MSP(s). Combined with the include-tenant list as OR. An include that resolves to no tenants matches nothing."
                  selected={includedMspCodes}
                  setSelected={setIncludedMspCodes}
                  catalog={user?.mspCatalog}
                  isProd={!editable}
                  tone="sky"
                />
                <MspScopeSelect
                  label="Exclude these MSPs"
                  help="Drops matches from every tenant of the selected MSP(s) before alert generation."
                  selected={excludedMspCodes}
                  setSelected={setExcludedMspCodes}
                  catalog={user?.mspCatalog}
                  isProd={!editable}
                  tone="amber"
                />
              </>
            )}

            {/* Included tenants — multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Include tenants</label>
              <p className="text-xs text-gray-500 mb-2">
                Empty = rule applies to all tenants. Non-empty = rule applies only to the selected tenants. Can coexist with Exclude; when both are set, Include narrows first and Exclude drops second.
              </p>
              {includedTenantIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {includedTenantIds.map((tid) => {
                    const t = tenantList.find((x) => x.TenantID === tid);
                    const label = t ? (t.FriendlyName || tid) : tid;
                    const title = t ? `${t.FriendlyName || '(unnamed)'} — ${tid}` : tid;
                    return (
                      <span key={tid} title={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-900/30 text-sky-300 border border-sky-700/40 text-xs">
                        <span>{label}</span>
                        <button
                          type="button"
                          onClick={() => editable && setIncludedTenantIds(includedTenantIds.filter((x) => x !== tid))}
                          disabled={!editable}
                          className={`text-sky-400 hover:text-sky-200 ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <select
                value=""
                disabled={!editable}
                onChange={(e) => {
                  const tid = e.target.value;
                  if (tid && !includedTenantIds.includes(tid)) {
                    setIncludedTenantIds([...includedTenantIds, tid]);
                  }
                }}
                className={`input-field w-full ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="">— add tenant —</option>
                {[...tenantList]
                  .filter((t) => !includedTenantIds.includes(t.TenantID))
                  .sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || ''))
                  .map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>
                      {t.FriendlyName || t.TenantID}
                    </option>
                  ))}
              </select>
            </div>

            {/* Excluded tenants — multi-select */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Exclude tenants</label>
              <p className="text-xs text-gray-500 mb-2">
                Matches from tenants in this list prevents the alert.
              </p>
              {excludedTenantIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {excludedTenantIds.map((tid) => {
                    const t = tenantList.find((x) => x.TenantID === tid);
                    const label = t ? (t.FriendlyName || tid) : tid;
                    const title = t ? `${t.FriendlyName || '(unnamed)'} — ${tid}` : tid;
                    return (
                      <span key={tid} title={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">
                        <span>{label}</span>
                        <button
                          type="button"
                          onClick={() => editable && setExcludedTenantIds(excludedTenantIds.filter((x) => x !== tid))}
                          disabled={!editable}
                          className={`text-amber-400 hover:text-amber-200 ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <select
                value=""
                disabled={!editable}
                onChange={(e) => {
                  const tid = e.target.value;
                  if (tid && !excludedTenantIds.includes(tid)) {
                    setExcludedTenantIds([...excludedTenantIds, tid]);
                  }
                }}
                className={`input-field w-full ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <option value="">— add tenant —</option>
                {[...tenantList]
                  .filter((t) => !excludedTenantIds.includes(t.TenantID))
                  .sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || ''))
                  .map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>
                      {t.FriendlyName || t.TenantID}
                    </option>
                  ))}
              </select>
            </div>

            {/* Excluded IP providers — chip/tag input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Exclude IPs from these providers</label>
              <p className="text-xs text-gray-500 mb-2">
                Matches whose IP enrichment shows any of these providers will be suppressed. Case-insensitive exact match — use the real provider name as it appears in IP enrichment (no wildcards). Press Enter to add.
              </p>
              {excludedIpProviders.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {excludedIpProviders.map((p, i) => (
                    <span key={`${p}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-mono">
                      <span>{p}</span>
                      <button
                        type="button"
                        onClick={() => editable && setExcludedIpProviders(excludedIpProviders.filter((_, idx) => idx !== i))}
                        disabled={!editable}
                        className={`text-amber-400 hover:text-amber-200 ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="text"
                value={providerInput}
                disabled={!editable}
                placeholder='e.g. Zscaler, Inc. — press Enter to add'
                onChange={(e) => setProviderInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && providerInput.trim()) {
                    e.preventDefault();
                    const v = providerInput.trim();
                    if (!excludedIpProviders.some((x) => x.toLowerCase() === v.toLowerCase())) {
                      setExcludedIpProviders([...excludedIpProviders, v]);
                    }
                    setProviderInput('');
                  }
                }}
                className={`input-field w-full ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          {/* Suppression toggles */}
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Suppression</label>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if Entra Joined</label>
                <p className="text-xs text-gray-500">Suppress alerts for Azure AD-joined and Hybrid Azure AD-joined devices.</p>
              </div>
              <button
                onClick={() => editable && setSkipIfEntraJoined(!skipIfEntraJoined)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfEntraJoined ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfEntraJoined ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if Intune Managed</label>
                <p className="text-xs text-gray-500">Suppress alerts for Intune-enrolled devices (compliant or not).</p>
              </div>
              <button
                onClick={() => editable && setSkipIfIntuneManaged(!skipIfIntuneManaged)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfIntuneManaged ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfIntuneManaged ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if Intune Compliant Device</label>
                <p className="text-xs text-gray-500">Suppress alerts when the event originates from a compliant device.</p>
              </div>
              <button
                onClick={() => editable && setSkipIfCompliantDevice(!skipIfCompliantDevice)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfCompliantDevice ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfCompliantDevice ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if DRMM Match</label>
                <p className="text-xs text-gray-500">Suppress alerts when the user has a DRMM-managed device.</p>
              </div>
              <button
                onClick={() => editable && setSkipIfDrmmMatch(!skipIfDrmmMatch)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfDrmmMatch ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfDrmmMatch ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if IP in MSP Tenant Baseline</label>
                <p className="text-xs text-gray-500">Suppress when event's IP is in MSP Tenant 7-day Baseline</p>
              </div>
              <button
                onClick={() => editable && setSkipIfInTenantBaseline(!skipIfInTenantBaseline)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInTenantBaseline ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInTenantBaseline ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if IP in User Baseline</label>
                <p className="text-xs text-gray-500">Suppress when event's IP is in the sign-in user's own baseline</p>
              </div>
              <button
                onClick={() => editable && setSkipIfInUserBaseline(!skipIfInUserBaseline)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInUserBaseline ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInUserBaseline ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if Provider in User Baseline</label>
                <p className="text-xs text-gray-500">Suppress when the event IP's provider/ASN is already established in the sign-in user's own baseline (rotating VPN/egress)</p>
              </div>
              <button
                onClick={() => editable && setSkipIfProviderInUserBaseline(!skipIfProviderInUserBaseline)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfProviderInUserBaseline ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfProviderInUserBaseline ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {skipIfProviderInUserBaseline && (
            <div className="flex items-center justify-between pl-4 border-l-2 border-gray-700">
              <div>
                <label className="block text-xs font-medium text-gray-400">Min distinct baseline IPs per provider</label>
                <p className="text-xs text-gray-600">How many baseline IPs must back a provider before it counts as established (default 2)</p>
              </div>
              <input
                type="number"
                min="1"
                value={providerBaselineMinIps}
                disabled={!editable}
                onChange={(e) => editable && setProviderBaselineMinIps(e.target.value === '' ? '' : Number(e.target.value))}
                className="input-field w-20 ml-4 disabled:opacity-50"
              />
            </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if CTAP Inbound Trust</label>
                <p className="text-xs text-gray-500">Honor resource tenant's CTAP inbound trust.<br />(MFA, compliant device, & hybrid join)</p>
              </div>
              <button
                onClick={() => editable && setSkipIfCtapTrust(!skipIfCtapTrust)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfCtapTrust ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfCtapTrust ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Skip if in Mailbox-Pair Allowlist</label>
                <p className="text-xs text-gray-500">Drops when every accessed (owner, actor) mailbox pair is on the Mailbox-Pair Allowlist.</p>
              </div>
              <button
                onClick={() => editable && setSkipIfInMailboxPairAllowlist(!skipIfInMailboxPairAllowlist)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInMailboxPairAllowlist ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInMailboxPairAllowlist ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Suppress Teams-phone device-code enrollments</label>
                <p className="text-xs text-gray-500">Drops a device-code sign-in when the account is allowlisted AND a Teams-phone registered within ~2 min. Malicious IPs still alert. Manage on the Account Allowlist + Teams-Phone Models pages.</p>
              </div>
              <button
                onClick={() => editable && setSkipIfTeamsPhoneEnrollment(!skipIfTeamsPhoneEnrollment)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfTeamsPhoneEnrollment ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfTeamsPhoneEnrollment ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (file hash, threat) Defender alerts</label>
                <p className="text-xs text-gray-500">Drops a Defender alert when a file-evidence hash is on the File-Hash Allowlist for the threat family (or a <span className="font-mono">*</span> entry). Malicious IPs still alert. <a href="/settings/defender-file-hash-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
              </div>
              <button
                onClick={() => editable && setSkipIfInFileHashAllowlist(!skipIfInFileHashAllowlist)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInFileHashAllowlist ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInFileHashAllowlist ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Suppress Microsoft-security alerts from allowlisted (benign) IPs</label>
                <p className="text-xs text-gray-500">When on, a Microsoft-security pass-through alert is suppressed if its source IP (sign-in IP or device IP) matches an entry in the Defender Pass-through IP Allowlist. Global across tenants. A known-malicious IP still alerts. <a href="/settings/benign-ip-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
              </div>
              <button
                onClick={() => editable && setSkipIfInDefenderPassthroughIpAllowlist(!skipIfInDefenderPassthroughIpAllowlist)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInDefenderPassthroughIpAllowlist ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInDefenderPassthroughIpAllowlist ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (user, application) pairs</label>
                <p className="text-xs text-gray-500">Drops a match whose (user, app) is on the User/App Allowlist (matched by app GUID). Malicious IPs still alert. <a href="/settings/user-app-pair-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
              </div>
              <button
                onClick={() => editable && setSkipIfInUserAppPairAllowlist(!skipIfInUserAppPairAllowlist)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInUserAppPairAllowlist ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInUserAppPairAllowlist ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (group, application) pairs</label>
                <p className="text-xs text-gray-500">Drops a match when the user is in an allowlisted group AND the app matches (membership refreshed daily). Malicious IPs still alert. <a href="/settings/group-app-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
              </div>
              <button
                onClick={() => editable && setSkipIfInGroupAppAllowlist(!skipIfInGroupAppAllowlist)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIfInGroupAppAllowlist ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIfInGroupAppAllowlist ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Match IPv6 Addresses by /64 Prefix</label>
                <p className="text-xs text-gray-500">Widens IPv6 comparison from exact to /64 prefix (same network).</p>
              </div>
              <button
                onClick={() => editable && setSkipIpv6PrefixMatch(!skipIpv6PrefixMatch)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${skipIpv6PrefixMatch ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  skipIpv6PrefixMatch ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-300">Alert if a Known Malicious IP</label>
                <p className="text-xs text-gray-500">
                  Fire the alert even when another Option would drop it.{' '}
                  <Link to="/settings/known-malicious-ips" className="text-blue-400 hover:text-blue-300 underline">
                    Manage list →
                  </Link>
                </p>
              </div>
              <button
                onClick={() => editable && setOverrideIfMaliciousIp(!overrideIfMaliciousIp)}
                disabled={!editable}
                className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors ${
                  !editable ? 'opacity-50 cursor-not-allowed' : ''
                } ${overrideIfMaliciousIp ? 'bg-blue-600' : 'bg-gray-700'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  overrideIfMaliciousIp ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          {/* Baseline window / IP matching mode — only relevant when user-baseline suppression is active */}
          {skipIfInUserBaseline && (
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Baseline</label>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Baseline window</label>
              <p className="text-xs text-gray-500 mb-2">
                How much sign-in history counts as "normal" for this user. 30-day captures occasional client sites, hotel Wi-Fi, and reduces false positives on "new IP" rules.
              </p>
              <select
                value={baselineWindow}
                disabled={!editable}
                onChange={(e) => setBaselineWindow(e.target.value)}
                className={`input-field w-full ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {BASELINE_WINDOW_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          )}

          {/* Editable fields — both modes (PROD lock removed) */}
          {editable && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="input-field w-full resize-y"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Severity</label>
                <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="input-field w-48">
                  {SEVERITY_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Cooldown (minutes)</label>
                <input
                  type="number"
                  min="0"
                  value={cooldownMinutes}
                  onChange={(e) => setCooldownMinutes(e.target.value)}
                  className="input-field w-32"
                />
                <p className="text-xs text-gray-500 mt-1">Dedup window in PROD mode. TEST mode has no cooldown.</p>
              </div>

              <hr className="border-gray-800" />

              {/* Parameters editor */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Parameters</label>
                {rule.pythonFunctionName === NEW_COUNTRY_SIGNIN_FN && (
                  <div className="mb-4 p-3 rounded border border-amber-700/30 bg-amber-900/10">
                    <label className="block text-xs text-gray-400 mb-1">Suppressed Event Countries</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Suppress new-country alerts when the event's country is in this list, regardless of baseline state.
                      ISO 2-letter codes (e.g. <span className="font-mono text-gray-300">US</span>, <span className="font-mono text-gray-300">GB</span>).
                      If <span className="font-mono text-gray-300">override_if_malicious_ip</span> is enabled on this rule, alerts whose source IP is in <span className="font-mono text-gray-300">KnownMaliciousIPs</span> still fire — threat intel takes precedence.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        list="edit-country-codes-list"
                        value={countryInput}
                        onChange={(e) => setCountryInput(e.target.value.toUpperCase().slice(0, 2))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addCountryCode();
                          }
                        }}
                        placeholder="ISO-2"
                        maxLength={2}
                        className="input-field w-24 font-mono uppercase"
                      />
                      <datalist id="edit-country-codes-list">
                        {COMMON_COUNTRY_CODES.map((c) => <option key={c} value={c} />)}
                      </datalist>
                      <button
                        type="button"
                        onClick={addCountryCode}
                        disabled={countryInput.trim().length !== 2}
                        className="btn-secondary text-xs px-3 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                    {suppressedCountryCodes.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {suppressedCountryCodes.map((code) => (
                          <span
                            key={code}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded bg-amber-900/30 border border-amber-700/40 text-amber-300"
                          >
                            {code}
                            <button
                              type="button"
                              onClick={() => removeCountryCode(code)}
                              className="text-amber-400 hover:text-amber-200"
                              title={`Remove ${code}`}
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {rule.ruleType === 'ip_flag_match' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">IP Flag</label>
                      <select value={ipFlag} onChange={(e) => setIpFlag(e.target.value)} className="input-field w-full">
                        <option value="">— Select —</option>
                        {IP_FLAG_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Event Types</label>
                      <div className="flex gap-4">
                        {EVENT_TYPE_OPTIONS.map((et) => (
                          <label key={et} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventTypes.includes(et)}
                              onChange={() => toggleEventType(et)}
                              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                            />
                            {et}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Additional Filters <span className="text-gray-600">(optional)</span></label>
                      {matchConditions.map((cond, idx) => {
                        const valueOpts = getFieldValueOptions(cond.field, rule.ruleType, logType);
                        return (
                        <div key={idx} className="flex gap-2 mb-2 items-center">
                          <select
                            value={cond.field}
                            onChange={(e) => updateMatchCondition(idx, 'field', e.target.value)}
                            className="input-field flex-1 text-xs"
                          >
                            <option value="">— Field —</option>
                            {FIELD_OPTIONS.map((f) => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                          <select
                            value={cond.operator}
                            onChange={(e) => updateMatchCondition(idx, 'operator', e.target.value)}
                            className="input-field w-28 text-xs"
                          >
                            {MATCH_OPERATORS.map((op) => (
                              <option key={op} value={op}>{op}</option>
                            ))}
                          </select>
                          {valueOpts ? (
                            <select
                              value={cond.value}
                              onChange={(e) => updateMatchCondition(idx, 'value', e.target.value)}
                              className="input-field flex-1 text-xs"
                            >
                              <option value="">— Value —</option>
                              {valueOpts.map((v) => (
                                <option key={v} value={v}>{v}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={cond.value}
                              onChange={(e) => updateMatchCondition(idx, 'value', e.target.value)}
                              placeholder="Value"
                              className="input-field flex-1 text-xs"
                            />
                          )}
                          <button
                            onClick={() => removeMatchCondition(idx)}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        );
                      })}
                      {matchConditions.some((c) => c.field === 'ResultStatus') && (() => {
                        const hint = getResultStatusHint(rule.ruleType, logType);
                        return hint ? (
                          <div className="mb-2 flex items-start gap-1.5 text-xs text-amber-400/80">
                            <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              <span className="font-medium text-amber-300">Result Status: </span>
                              {hint.text}
                              {hint.hasRef && (
                                <button onClick={() => setShowResultStatusRef(true)} className="ml-1 text-blue-400 hover:text-blue-300 underline">
                                  View reference →
                                </button>
                              )}
                            </span>
                          </div>
                        ) : null;
                      })()}
                      <button
                        onClick={addMatchCondition}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        + Add filter
                      </button>
                    </div>
                  </div>
                )}
                {rule.ruleType === 'log_type_match' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Log Type</label>
                      <select value={logType} onChange={(e) => setLogType(e.target.value)} className="input-field w-full">
                        <option value="">— Select —</option>
                        {LOG_TYPE_OPTIONS.map((lt) => (
                          <option key={lt.value} value={lt.value}>{lt.label} ({lt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Match Conditions</label>
                      {matchConditions.map((cond, idx) => {
                        const valueOpts = getFieldValueOptions(cond.field, rule.ruleType, logType);
                        const isJsonPath = cond.field.startsWith('$.');
                        return (
                        <div key={idx} className="mb-2">
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              list={`lt-field-options-${idx}`}
                              value={cond.field}
                              onChange={(e) => updateMatchCondition(idx, 'field', e.target.value)}
                              placeholder="Field or $.jsonPath"
                              className={`input-field flex-1 text-xs${isJsonPath ? ' border-amber-700/50' : ''}`}
                            />
                            <datalist id={`lt-field-options-${idx}`}>
                              {FIELD_OPTIONS.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                              {JSON_PATH_FIELD_OPTIONS.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                            </datalist>
                            <select
                              value={cond.operator}
                              onChange={(e) => updateMatchCondition(idx, 'operator', e.target.value)}
                              className="input-field w-28 text-xs"
                            >
                              {MATCH_OPERATORS.map((op) => (
                                <option key={op} value={op}>{op}</option>
                              ))}
                            </select>
                            {valueOpts ? (
                              <select
                                value={cond.value}
                                onChange={(e) => updateMatchCondition(idx, 'value', e.target.value)}
                                className="input-field flex-1 text-xs"
                              >
                                <option value="">— Value —</option>
                                {valueOpts.map((v) => (
                                  <option key={v} value={v}>{v}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                value={cond.value}
                                onChange={(e) => updateMatchCondition(idx, 'value', e.target.value)}
                                placeholder="Value"
                                className="input-field flex-1 text-xs"
                              />
                            )}
                            <button
                              onClick={() => removeMatchCondition(idx)}
                              className="text-red-400 hover:text-red-300 text-xs px-1"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {isJsonPath && (
                            <p className="text-[10px] text-amber-500/80 mt-0.5 ml-0.5">
                              JSON path — matched as a substring of the raw event field. Case-sensitive; not lowercased by pipeline.
                            </p>
                          )}
                        </div>
                        );
                      })}
                      {(() => {
                        const hint = getFieldSyntaxHint(logType);
                        return hint ? (
                          <div className="mb-2 flex items-start gap-1.5 text-xs text-gray-400">
                            <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-blue-400" />
                            <span>
                              <span className="font-medium text-gray-300">Field syntax: </span>
                              Top-level columns are case-insensitive (e.g. <code className="text-gray-300">UserId</code>).
                              JSON paths must be <span className="text-amber-300">{hint.caseStyle}</span> and prefixed with <code className="text-gray-300">$.</code> —
                              {' '}{hint.api} example: <code className="text-gray-300">{hint.example}</code>.
                            </span>
                          </div>
                        ) : null;
                      })()}
                      {matchConditions.some((c) => c.field === 'ResultStatus') && (() => {
                        const hint = getResultStatusHint(rule.ruleType, logType);
                        return hint ? (
                          <div className="mb-2 flex items-start gap-1.5 text-xs text-amber-400/80">
                            <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                            <span>
                              <span className="font-medium text-amber-300">Result Status: </span>
                              {hint.text}
                              {hint.hasRef && (
                                <button onClick={() => setShowResultStatusRef(true)} className="ml-1 text-blue-400 hover:text-blue-300 underline">
                                  View reference →
                                </button>
                              )}
                            </span>
                          </div>
                        ) : null;
                      })()}
                      <button
                        onClick={addMatchCondition}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        + Add condition
                      </button>
                    </div>
                  </div>
                )}
                {rule.ruleType === 'baseline_deviation' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Baseline Field <span className="text-red-400">*</span></label>
                      <select value={baselineField} onChange={(e) => setBaselineField(e.target.value)} className="input-field w-full">
                        <option value="country_list">Country List — flag sign-ins from unknown countries</option>
                        <option value="signin_ip_list">Sign-in IP List — flag sign-ins from unknown IPs</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Event Types</label>
                      <div className="flex gap-4">
                        {EVENT_TYPE_OPTIONS.map((et) => (
                          <label key={et} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventTypes.includes(et)}
                              onChange={() => toggleEventType(et)}
                              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                            />
                            {et}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Which sign-in log shapes this rule evaluates against the baseline.</p>
                    </div>
                  </div>
                )}
                {rule.ruleType === 'operation_baseline_deviation' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Log Type <span className="text-red-400">*</span></label>
                      <select value={opBaselineLogType} onChange={(e) => setOpBaselineLogType(e.target.value)} className="input-field w-full">
                        <option value="">— Select —</option>
                        {LOG_TYPE_OPTIONS.map((lt) => (
                          <option key={lt.value} value={lt.value}>{lt.label} ({lt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Operations <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={opBaselineOperation}
                        onChange={(e) => setOpBaselineOperation(e.target.value)}
                        placeholder="e.g. New-InboxRule, Set-InboxRule, UpdateInboxRules"
                        className="input-field w-full"
                      />
                      <p className="text-xs text-gray-600 mt-1">Comma-separated. Matches any of the listed operations.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Baseline Field</label>
                      <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">Sign-in IP List</p>
                      <p className="text-xs text-gray-600 mt-1">Events are flagged when the client IP is not in the user's historical sign-in IP baseline across all device types.</p>
                    </div>
                  </div>
                )}
                {(rule.ruleType === 'python_function' || rule.ruleType === 'python_function_config') && (
                  <div>
                    {rule.pythonFunctionName === 'rule_local_admin_group_change' && (
                      <DomainJoinToggle rawParamsJson={rawParamsJson} setRawParamsJson={setRawParamsJson} />
                    )}
                    {rule.ruleType === 'python_function_config' && (
                      <p className="text-xs text-gray-500 mb-1.5">
                        Pipeline-defined parameters. Edit JSON directly; unknown keys are preserved on save.
                      </p>
                    )}
                    <textarea
                      value={rawParamsJson}
                      onChange={(e) => setRawParamsJson(e.target.value)}
                      rows={8}
                      className="input-field w-full font-mono text-xs resize-y"
                      placeholder="{}"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Read-only display retained for the locked state (currently never shown — PROD lock removed) */}
          {!editable && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                <ExclamationTriangleIcon className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-300">
                  PROD rules have limited editing. Demote to TEST to change rule configuration.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Description</label>
                <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">{rule.description || '—'}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Severity</label>
                <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50 capitalize">{rule.severity}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Cooldown</label>
                <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">{rule.cooldownMinutes} minutes</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Parameters</label>
                {(() => {
                  let params = {};
                  try { params = JSON.parse(rule.parametersJson || '{}'); } catch {}
                  const conditions = Array.isArray(params.match_conditions) ? params.match_conditions : [];
                  const rest = Object.fromEntries(Object.entries(params).filter(([k]) => k !== 'match_conditions'));
                  return (
                    <>
                      {conditions.length > 0 && (
                        <div className="mb-2">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Match Conditions</p>
                          <div className="space-y-1">
                            {conditions.map((c, i) => (
                              <div key={i} className="flex items-center gap-1.5 font-mono text-xs bg-gray-800/50 px-3 py-1.5 rounded border border-gray-700/50">
                                <span className={c.field && c.field.startsWith('$.') ? 'text-amber-300' : 'text-sky-300'}>{c.field}</span>
                                <span className="text-gray-500">{c.operator}</span>
                                <span className="text-green-300">"{c.value}"</span>
                                {c.field && c.field.startsWith('$.') && (
                                  <span className="ml-auto text-[9px] text-amber-500/70 font-sans">JSON path</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <pre className="text-xs text-gray-400 font-mono bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50 whitespace-pre-wrap overflow-x-auto">
                        {JSON.stringify(Object.keys(rest).length > 0 ? rest : params, null, 2)}
                      </pre>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Alert de-dup granularity — editable in BOTH TEST and PROD (outside the
              branches). Saved by the normal drawer Save; the API accepts dedupOnEventId
              on PROD rules without demotion because it changes only alert-collapse
              behavior, not detection logic. */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-800">
            <div className="pr-4">
              <label className="block text-sm font-medium text-gray-300">Alert on every matching event</label>
              <p className="text-xs text-gray-500">
                When off (default), repeated matches for the same user collapse to one alert per
                cooldown window. Turn on only for rules that should surface every distinct source
                event (e.g. Microsoft-security pass-through rules that emit several alerts for one
                user in a burst). With this on, the cooldown does not collapse per-user duplicates.
              </p>
            </div>
            <button
              onClick={() => setDedupOnEventId(!dedupOnEventId)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                dedupOnEventId ? 'bg-blue-600' : 'bg-gray-700'
              }`}
              title={dedupOnEventId ? 'Alerting on every distinct event' : 'Collapsing to one alert per user per cooldown'}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                dedupOnEventId ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Excluded Alert Titles — Defender pass-through rules only. Outside the
              PROD/TEST branches so it works in both (saves via its own endpoint, not
              the PROD-disabled drawer Save). */}
          {rule?.pythonFunctionName === DEFENDER_PASSTHROUGH_FN && (
            <>
              <ExcludedAlertTitlesManager rule={rule} />
              <ExcludedAlertDescriptionsManager rule={rule} />
              <ExcludedAlertActorsManager rule={rule} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 space-y-3">
          {saveError && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{saveError}</p>
            </div>
          )}

          {/* Scheduled testing toggle (TEST mode only) */}
          {isTest && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300 font-medium">Scheduled Testing</p>
                <p className="text-xs text-gray-500">
                  {tested
                    ? 'This rule runs every 30 min. Results go to Test Results only.'
                    : 'This rule only runs when you trigger a test manually.'}
                </p>
              </div>
              <button
                onClick={handleTestedToggle}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  tested ? 'bg-green-600' : 'bg-gray-700'
                }`}
                title={tested ? 'Disable scheduled testing' : 'Enable scheduled testing'}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  tested ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          )}

          {/* Mode transition button */}
          {isTest && (
            <button
              onClick={() => handleModeChange('PROD')}
              disabled={saving}
              className="w-full px-4 py-2 text-sm font-medium rounded-lg bg-green-700 hover:bg-green-600 text-white transition-colors disabled:opacity-50"
            >
              Promote to PROD
            </button>
          )}
          {isProd && (
            <button
              onClick={() => handleModeChange('TEST')}
              disabled={saving}
              className="w-full px-4 py-2 text-sm font-medium rounded-lg bg-amber-700 hover:bg-amber-600 text-white transition-colors disabled:opacity-50"
            >
              Demote to TEST
            </button>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={saving}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {/* Last updated info */}
          {rule.updatedUtc && (
            <p className="text-xs text-gray-600 text-center">
              Last updated {new Date(rule.updatedUtc).toLocaleString()} by {rule.updatedBy || '—'}
            </p>
          )}

          {/* Engineering debug: hover reveals copy buttons */}
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-gray-600 font-mono">
            <span className="group inline-flex items-center">
              ID: {rule.ruleId}
              <CopyButton text={rule.ruleId} />
            </span>
            {rule.legacyRuleId && (
              <span className="group inline-flex items-center">
                Legacy: {rule.legacyRuleId}
                <CopyButton text={rule.legacyRuleId} />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ResultStatus Reference Panel — slides in to the left of the drawer */}
      {showResultStatusRef && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setShowResultStatusRef(false)} />
          <div className="fixed right-[480px] top-0 h-full w-[480px] bg-gray-900 border-l border-gray-700 shadow-2xl z-[60] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <div>
                <h3 className="text-sm font-semibold text-gray-100">ResultStatus — general log type</h3>
                <p className="text-xs text-gray-500 mt-0.5">Verified from data 2026-04-08. Values vary by operation type, not by product alone.</p>
              </div>
              <button onClick={() => setShowResultStatusRef(false)} className="text-gray-400 hover:text-gray-200">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {GENERAL_RESULT_STATUS_REFERENCE.map((group) => (
                <div key={group.value} className="space-y-1.5">
                  <code className="inline-block px-2 py-0.5 text-xs font-mono bg-gray-800 text-blue-300 border border-gray-700 rounded">
                    {group.value}
                  </code>
                  <p className="text-xs text-gray-400">{group.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {group.examples.map((ex) => (
                      <span key={ex} className="px-1.5 py-0.5 text-xs font-mono bg-gray-800/60 text-gray-500 border border-gray-700/50 rounded">
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
